/**
 * `checkbox` — a centred check glyph that toggles on a **single click**, no popover
 * (M2 §5.12, §6). `Space` toggles; `Delete` clears the cell entirely, which is distinct from
 * unchecking (§5.12): `{checked:false}` is a stored value, no row at all is empty.
 */
import { getColumnType, type CheckboxSettings, type CheckboxValue } from '@elizmonday/shared';
import { defineColumnUi } from '../types';
import { sharedBuckets } from '../buckets';
import { CheckboxSettingsDialog } from './SettingsDialog';

const shared = getColumnType('checkbox');

export const checkboxColumnUi = defineColumnUi<CheckboxValue, CheckboxSettings>({
  type: 'checkbox',
  icon: 'columnCheckbox',
  gallery: { group: 'essentials', keywords: ['checkbox', 'tick', 'boolean', 'done'] },
  /** P-022: the registry default. ASSUMED — no `width: null` column of this type is measured. */
  defaultWidth: 140,
  align: 'center',
  groupable: true,
  /** P-263: the value is item-independent, so it may be written to a whole selection. */
  massEditable: true,
  // One click toggles, with no selection step first (`cell-normal` → `cell-toggled`).
  activateOnFirstClick: true,

  Cell: ({ value }) => (
    <span className="ez-checkbox-cell" data-checked={value?.checked ? '' : undefined} aria-hidden="true">
      {/* Captured: the tick is monday's `done-green`, drawn without a surrounding box. */}
      <span className="ez-checkbox-cell__box">{value?.checked ? '✓' : ''}</span>
    </span>
  ),

  /**
   * The editor exists so `Enter` and a click on the focused cell have something to open, but it
   * commits immediately and closes — which is exactly "toggles on a single click".
   */
  Editor: ({ value, onCommit }) => {
    queueMicrotask(() => onCommit({ checked: !(value?.checked ?? false) } as CheckboxValue));
    return null;
  },

  SettingsDialog: CheckboxSettingsDialog,

  fromClipboard(text, settings) {
    const trimmed = text.trim();
    if (trimmed === '') return null;
    return shared.parseValue(trimmed, settings) as CheckboxValue | null;
  },
  toClipboard(value, settings) {
    return shared.toText(value, settings) ?? '';
  },
  bucketsOf: sharedBuckets('checkbox'),
});
